import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import {
	GITLAB_BASE_URL,
	GITLAB_GRAPHQL_URL,
	REFRESH_INTERVAL,
} from "../constants";
import { useCachedStorage } from "./useCachedStorage";

// Create dynamic GraphQL query based on project IDs
const createMRCheckQuery = (projectBranches) => {
	const fragments = Object.entries(projectBranches).map(
		([projectId, branches], index) => `
    ${String.fromCharCode(97 + index)}: projects(ids: ["gid://gitlab/Project/${projectId}"], first: 1) {
      nodes {
        id
        webUrl
        fullPath
        mergeRequests(sourceBranches: ${JSON.stringify(branches)}) {
          nodes {
            id
            sourceBranch
            closedAt
            mergedAt
          }
        }
      }
    }
  `,
	);

	return `query checkIfMrExists {
    ${fragments.join("\n")}
  }`;
};

export const useRecentPushes = () => {
	const { user } = useAuth();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [pushesWithoutMR, setPushesWithoutMR] = useCachedStorage(
		"recent-pushes-without-mr-cache",
		[],
		24,
	);
	const pushesWithoutMRRef = useRef(pushesWithoutMR);

	useEffect(() => {
		pushesWithoutMRRef.current = pushesWithoutMR;
	}, [pushesWithoutMR]);

	const fetchPushes = useCallback(async () => {
		if (!user?.access_token || !user.profile.id) return;

		try {
			// Get yesterday's date in YYYY-MM-DD format
			const yesterday = new Date();
			yesterday.setDate(yesterday.getDate() - 1);
			const dateStr = yesterday.toISOString().split("T")[0];

			// Fetch recent pushes with project details
			const pushResponse = await fetch(
				`${GITLAB_BASE_URL}/api/v4/users/${user.profile.id}/events?action=pushed&after=${dateStr}&per_page=50&include_project_details=true`,
				{
					headers: { Authorization: `Bearer ${user.access_token}` },
				},
			);

			if (!pushResponse.ok) {
				throw new Error(`Failed to fetch push events: ${pushResponse.status}`);
			}

			const pushEvents = await pushResponse.json();

			const pushEventsFiltered = pushEvents
				.filter(
					(e) =>
						e.action_name === "pushed new" || e.action_name === "pushed to",
				)
				.filter((e) => e.push_data.ref_type === "branch");

			// Track latest push date for each branch
			const uniquePushes = pushEventsFiltered.reduce((acc, event) => {
				const key = `${event.project_id}-${event.push_data.ref}`;
				const eventDate = new Date(event.created_at);

				if (!acc[key] || eventDate > new Date(acc[key].created_at)) {
					acc[key] = event;
				}
				return acc;
			}, {});

			const deduplicatedPushes = Object.values(uniquePushes);

			// Group pushes by project
			const projectPushes = deduplicatedPushes.reduce((acc, event) => {
				if (!acc[event.project_id]) {
					acc[event.project_id] = [];
				}
				acc[event.project_id].push(event.push_data.ref);
				return acc;
			}, {});

			// If no pushes, return early
			if (Object.keys(projectPushes).length === 0) {
				setPushesWithoutMR([]);
				setLoading(false);
				return;
			}

			// Check for existing MRs with GraphQL query
			const query = createMRCheckQuery(projectPushes);
			const mrResponse = await fetch(GITLAB_GRAPHQL_URL, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${user.access_token}`,
				},
				body: JSON.stringify({ query }),
			});

			if (!mrResponse.ok) {
				throw new Error(
					`Failed to fetch merge request data: ${mrResponse.status}`,
				);
			}

			const mrResult = await mrResponse.json();

			if (mrResult.errors) {
				console.error("GraphQL Errors:", mrResult.errors);
				throw new Error("GraphQL query failed");
			}

			// Extract existing MRs and project info
			const branchMRStatus = new Map(); // Track MR status for each branch
			const projectInfo = {};

			// Process GraphQL response data
			for (const projectsWrapper of Object.values(mrResult.data || {})) {
				if (!projectsWrapper?.nodes?.length) continue;

				const project = projectsWrapper.nodes[0]; // We only query one project per alias
				if (!project) continue;

				// Extract project ID from the returned GraphQL id
				const projectId = project.id.split("/").pop();

				projectInfo[projectId] = {
					webUrl: project.webUrl,
					fullPath: project.fullPath,
				};

				// Store MR status for each branch
				for (const mr of project.mergeRequests?.nodes || []) {
					if (!mr.sourceBranch) continue;

					const lastActionDate = mr.closedAt || mr.mergedAt;
					if (lastActionDate) {
						const newDate = new Date(lastActionDate);
						const existingDate = branchMRStatus.get(mr.sourceBranch);
						// Only update if there's no existing date or if new date is more recent
						if (
							!existingDate ||
							(existingDate !== true && newDate > existingDate)
						) {
							branchMRStatus.set(mr.sourceBranch, newDate);
						}
					} else {
						// If there's an open MR (no close/merge date), always mark as true
						branchMRStatus.set(mr.sourceBranch, true);
					}
				}
			}

			const newPushes = deduplicatedPushes
				.filter((event) => {
					if (
						event.push_data.ref === "main" ||
						event.push_data.ref === "master" ||
						event.push_data.ref === "staging" ||
						event.push_data.ref === "prod" ||
						event.push_data.ref === "production"
					) {
						return false;
					}

					const mrStatus = branchMRStatus.get(event.push_data.ref);

					if (!mrStatus) {
						return true; // No MR exists for this branch
					}
					if (mrStatus === true) {
						return false; // There exists an open MR for this branch
					}

					// Check if the push is newer than when the MR was closed/merged
					const pushDate = new Date(event.created_at);
					return mrStatus ? pushDate > mrStatus : true;
				})
				.map((event) => {
					const mappedProjectInfo = projectInfo[event.project_id];
					if (!mappedProjectInfo) {
						console.warn(
							`No project info found for project ID ${event.project_id}. Using fallback data.`,
						);
					}

					return {
						...event,
						projectInfo: mappedProjectInfo || {
							webUrl: event.project?.web_url || "",
							fullPath: event.project?.path_with_namespace || "",
						},
					};
				});

			setPushesWithoutMR(newPushes);
			setError(null);
		} catch (err) {
			console.error("Error fetching push data:", err);
			setError(
				pushesWithoutMRRef.current.length
					? `Showing cached push data. Last refresh failed: ${err.message}`
					: err.message,
			);
		} finally {
			setLoading(false);
		}
	}, [user, setPushesWithoutMR]);

	// Initial fetch
	useEffect(() => {
		if (user?.access_token) {
			fetchPushes();
		}
	}, [user, fetchPushes]);

	// Periodic refresh
	useEffect(() => {
		if (!user?.access_token) return;

		const intervalId = setInterval(fetchPushes, REFRESH_INTERVAL);
		return () => clearInterval(intervalId);
	}, [user, fetchPushes]);

	return { pushesWithoutMR, loading, error };
};
